import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

import { queryClient } from 'app/queryClient';
import { api, type ApiRequestConfig } from 'api/v1/api';

export type AuthSession = {
  authenticated: boolean;
  id: number;
  email: string;
  phone?: string | null;
  phone_verified_at?: string | null;
  email_verified_at?: string | null;
  email_verified?: boolean;
  phone_verified?: boolean;
  has_password?: boolean;
  roles: number[];
};

export const AUTH_SESSION_QUERY_KEY = ['auth', 'session'] as const;

const EMPTY_SESSION: AuthSession = {
  authenticated: false,
  id: 0,
  email: '',
  roles: [],
  has_password: false,
  email_verified: false,
  phone_verified: false,
};

export const fetchAuthSession = async (): Promise<AuthSession | null> => {
  try {
    const { data } = await api.get<AuthSession>('/auth/session/', {
      skipGlobalErrorHandler: true,
    } as ApiRequestConfig);
    if (!data?.authenticated) {
      return null;
    }
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      return null;
    }
    throw error;
  }
};

export const useAuthSession = (enabled = true) => {
  return useQuery({
    queryKey: AUTH_SESSION_QUERY_KEY,
    queryFn: fetchAuthSession,
    enabled,
    retry: (failureCount, error) => {
      if (!axios.isAxiosError(error)) return false;
      if (error.response?.status === 401) return false;
      // Таймаут и обрыв сети не повторяем: иначе /login остается на спиннере.
      if (
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_NETWORK' ||
        error.code === 'ETIMEDOUT' ||
        !error.response
      ) {
        return false;
      }
      return error.response.status >= 500 && failureCount < 1;
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: true,
    // Do not keep a previous user's session as placeholder across logout/login.
  });
};

export const isAuthSessionActive = (
  session: AuthSession | null | undefined,
): boolean => Boolean(session?.authenticated);

/** Синхронизирует кэш сессии сразу после login/register - до navigate, чтобы RoleGuard не отбрасывал на /login. */
export const applyAuthSession = (data: {
  id: number;
  email?: string | null;
  phone?: string | null;
  roles?: number[] | null;
  phone_verified_at?: string | null;
  email_verified_at?: string | null;
  email_verified?: boolean;
  phone_verified?: boolean;
  has_password?: boolean;
}): void => {
  const roles = Array.isArray(data.roles) ? data.roles : [];
  queryClient.setQueryData<AuthSession | null>(AUTH_SESSION_QUERY_KEY, {
    authenticated: true,
    id: data.id,
    email: data.email ?? '',
    phone: data.phone ?? null,
    phone_verified_at: data.phone_verified_at ?? null,
    email_verified_at: data.email_verified_at ?? null,
    email_verified: Boolean(data.email_verified),
    phone_verified: Boolean(data.phone_verified ?? data.phone_verified_at),
    has_password: Boolean(data.has_password),
    roles,
  });
};

export const clearAuthSessionCache = (): void => {
  queryClient.setQueryData<AuthSession | null>(AUTH_SESSION_QUERY_KEY, EMPTY_SESSION);
};
